import { useCallback, useMemo } from "react";
import type { UseWizardProps, WizardAPI } from "./types";
import { useControlledWizard } from "./useControlledWizard";
import { useQueryParamState } from "./useQueryParamState";

export type WizardOptions = {
  queryParamKey?: string;
};

type UseWizardWithQueryParamArgs<TStep extends string | number | symbol> =
  UseWizardProps<TStep> & {
    options?: WizardOptions;
  };

export const useWizardWithQueryParam = <
  TStep extends string | number | symbol,
>({
  steps,
  onStepChange,
  onComplete,
  options: { queryParamKey } = {},
}: UseWizardWithQueryParamArgs<TStep>): WizardAPI<TStep> => {
  const [queryParam, setQueryParam] = useQueryParamState<string>(
    queryParamKey ?? "step",
    steps[0]?.toString() ?? ""
  );

  const currentStepIndex = useMemo(() => {
    const index = steps.findIndex((step) => step.toString() === queryParam);
    return index === -1 ? 0 : index;
  }, [queryParam, steps]);

  const setCurrentStepIndex = useCallback(
    (updater: (previousIndex: number) => number) =>
      setQueryParam((step) => {
        const nextIndex = updater(
          steps.findIndex((item) => item.toString() === step)
        );
        return steps[nextIndex]?.toString() ?? "";
      }),
    [setQueryParam, steps]
  );

  return useControlledWizard({
    steps,
    currentStepIndex,
    setCurrentStepIndex,
    onStepChange,
    onComplete,
  });
};
